import { useState, type CSSProperties } from 'react';
import { OS_TABS, CLAUDE_CODE_COMMANDS, detectOS, type OS } from '../../lib/claude-code-install';

/**
 * OS-aware Claude Code (terum-capture CLI) install instructions for the Sources
 * "Connect Claude Code" modal. The OS→command mapping + detection live in
 * lib/claude-code-install (shared with the onboarding wizard's ClaudeCodeStep so
 * the commands never drift); this component owns the Sources-modal presentation.
 *
 * The tab defaults to the visitor's detected OS; they can switch if setting up on a
 * different machine.
 */

const STEP_LABEL: CSSProperties = {
  fontFamily: 'var(--mono)',
  fontSize: 11,
  letterSpacing: '0.10em',
  color: 'var(--text3)',
  textTransform: 'uppercase',
  marginBottom: 6,
};

// bug-281: lay the command and the copy button out as a flex row so the button
// can never overlap the command text (the old position:absolute + nowrap layout
// let long commands scroll underneath the button).
function CodeBlock({ children }: { children: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 10,
        background: 'var(--bg)',
        border: '1px solid var(--border)',
        borderRadius: 6,
        padding: '10px 12px',
        fontFamily: 'var(--mono)',
        fontSize: 13,
        color: 'var(--green)',
        letterSpacing: '0.02em',
      }}
    >
      <code style={{ flex: 1, minWidth: 0, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
        {children}
      </code>
      <button
        onClick={e => {
          e.stopPropagation();
          navigator.clipboard.writeText(children);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
        style={{
          flex: '0 0 auto',
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          fontFamily: 'var(--mono)',
          fontSize: 11,
          color: copied ? 'var(--green)' : 'var(--text3)',
          padding: '2px 6px',
        }}
      >
        {copied ? 'copied' : 'copy'}
      </button>
    </div>
  );
}

export function ClaudeCodeInstall() {
  const [os, setOs] = useState<OS>(detectOS);
  const cmd = CLAUDE_CODE_COMMANDS[os];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, marginBottom: 20 }}>
      <div role="tablist" aria-label="Operating system" style={{ display: 'flex', gap: 6 }}>
        {OS_TABS.map(({ key, label }) => {
          const active = key === os;
          return (
            <button
              key={key}
              role="tab"
              aria-selected={active}
              onClick={e => {
                e.stopPropagation();
                setOs(key);
              }}
              style={{
                flex: 1,
                padding: '7px 10px',
                borderRadius: 6,
                cursor: 'pointer',
                fontFamily: 'var(--mono)',
                fontSize: 11,
                letterSpacing: '0.06em',
                background: active ? 'rgba(133,112,255,0.10)' : 'transparent',
                border: `1px solid ${active ? 'var(--accent)' : 'var(--border)'}`,
                color: active ? 'var(--text)' : 'var(--text3)',
                transition:
                  'color var(--dur-fast), border-color var(--dur-fast), background var(--dur-fast)',
              }}
            >
              {label}
            </button>
          );
        })}
      </div>

      <div>
        <div style={STEP_LABEL}>1. Install</div>
        <CodeBlock>{cmd.install}</CodeBlock>
      </div>

      <div>
        <div style={STEP_LABEL}>2. Authenticate</div>
        <CodeBlock>{cmd.auth}</CodeBlock>
        <div
          style={{
            fontFamily: 'var(--sans)',
            fontSize: 12,
            color: 'var(--text3)',
            marginTop: 6,
            lineHeight: 1.5,
          }}
        >
          Opens your browser to log in, then auto-configures the Claude Code hook.
        </div>
      </div>

      <div
        style={{
          fontFamily: 'var(--sans)',
          fontSize: 12,
          color: 'var(--text2)',
          lineHeight: 1.5,
          padding: '10px 14px',
          background: 'rgba(133,112,255,0.06)',
          borderRadius: 6,
          border: '1px solid rgba(133,112,255,0.12)',
        }}
      >
        After setup, every Claude Code session is captured automatically. This source will show as
        active once your first session is recorded.
      </div>
    </div>
  );
}
